import React, { useState, useEffect, useCallback } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import Pager from '../components/Pager';
import useResourceWatch from '../hooks/useResourceWatch';
import { useNamespaces, NamespaceSelect } from './LivePage';
import { useAuth } from '../context/AuthContext';
import DeployImageModal from '../components/DeployImageModal';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import WebTerminal from '../components/WebTerminal';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';
import './ResourcePage.css';

function Deployments() {
  const { selectedCluster } = useCluster();
  const navigate = useNavigate();
  const { hasSectionAction } = useAuth();
  const canCreate = hasSectionAction('workloads', 'create');
  const canEdit   = hasSectionAction('workloads', 'edit');
  const canDelete = hasSectionAction('workloads', 'delete');
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [restartingKey, setRestartingKey] = useState(null);
  // Server-side namespace scoping (fast) — replaces the old client-side 'all' filter.
  const { namespaces, ns, setNs } = useNamespaces(selectedCluster?.id);
  const [nameFilter, setNameFilter]   = useState('');
  const [showDeploy, setShowDeploy]   = useState(false);
  // Live configuration for one deployment (SARVKUBE-63). Not gated on canEdit:
  // reading how a workload is configured needs view access, not edit.
  const [detail, setDetail]           = useState(null);
  const [showTerminal, setShowTerminal] = useState(false);
  // Server-side paging via Steve. `paged` records whether the current data came from the
  // paged endpoint, so the pager is only shown when it can actually page.
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal]       = useState(0);
  const [paged, setPaged]       = useState(false);

  const loadDeployments = useCallback(async () => {
    if (!selectedCluster?.is_imported) return;
    // Wait until the namespace is chosen before fetching, so we never fire an
    // all-namespaces (-A) query on first render (the slow path). `ns` resolves to
    // 'default' (or the first namespace) as soon as the namespace list loads.
    if (!ns) return;
    setLoading(true);
    setError(null);
    try {
      // Prefer the paged endpoint (Steve): one page is ~0.5s regardless of how many
      // deployments exist. It is Rancher-only, so fall back to the full list for agent and
      // kubeconfig clusters rather than showing them an error.
      try {
        const { data } = await clustersAPI.getDeploymentsPaged(selectedCluster.id, {
          page, pageSize, namespace: ns,
        });
        setDeployments(data.items || []);
        setTotal(data.total ?? (data.items || []).length);
        setPaged(true);
        return;
      } catch (pagedErr) {
        if (pagedErr?.response?.status !== 404) throw pagedErr;
      }

      const res = await clustersAPI.getDeployments(selectedCluster.id, ns);
      // The deployments endpoint returns a BARE ARRAY for direct/kubeconfig clusters but
      // an object `{items:[...]}` for AGENT clusters (the Go agent's /sarv/deployments
      // shape). Normalize to an array so `.map` never crashes ("a.map is not a function").
      const d = res.data;
      const arr = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []);
      setDeployments(arr);
      setTotal(arr.length);
      setPaged(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to fetch deployments');
    } finally {
      setLoading(false);
    }
  // Depend on the specific fields actually used (id, is_imported), not the whole
  // `selectedCluster` object — that object gets a NEW reference on every cluster-list
  // refresh (even when nothing about it changed), which re-ran this effect and reloaded
  // the whole page every 30s via AuthContext's permissions-refresh -> ClusterContext's
  // re-select cascade. Confirmed live: the page was visibly "auto-refreshing" on its own.
  }, [selectedCluster?.id, selectedCluster?.is_imported, ns, page, pageSize]);

  useEffect(() => { loadDeployments(); }, [loadDeployments]);

  // A namespace or page-size change invalidates the current page number.
  useEffect(() => { setPage(1); }, [ns, pageSize, selectedCluster?.id]);

  // ── Live updates ───────────────────────────────────────────────────────────
  // Replica counts change constantly during a rollout, which is exactly when someone is
  // watching this page. Patch rows in place rather than re-polling.
  const [pendingNew, setPendingNew] = useState(0);

  const patchRow = useCallback((key, flat) => {
    setDeployments(prev => {
      const i = prev.findIndex(d => `${d.namespace}/${d.name}` === key);
      if (i === -1) return prev;                 // not on this page
      const next = prev.slice();
      next[i] = { ...next[i], ...flat };
      return next;
    });
  }, []);

  const removeRow = useCallback((key) => {
    setDeployments(prev => {
      const next = prev.filter(d => `${d.namespace}/${d.name}` !== key);
      if (next.length === prev.length) return prev;
      setTotal(t => Math.max(0, t - 1));
      return next;
    });
  }, []);

  const noteNew = useCallback((key, flat) => {
    // Steve re-sends creates on reconnect, so a "create" for a row we already show is a
    // patch, not a new arrival.
    setDeployments(prev => {
      if (prev.some(d => `${d.namespace}/${d.name}` === key)) {
        return prev.map(d => (`${d.namespace}/${d.name}` === key ? { ...d, ...flat } : d));
      }
      setPendingNew(n => n + 1);
      return prev;
    });
  }, []);

  const { connected: liveConnected } = useResourceWatch(selectedCluster?.id, 'deployments', {
    // Only when the data actually came from Steve — an agent/kubeconfig cluster fell back
    // to the full list and has no watch to attach to.
    enabled: paged && !!selectedCluster?.id,
    namespace: ns || undefined,
    onPatch: patchRow,
    onRemove: removeRow,
    onNew: noteNew,
  });

  useEffect(() => { setPendingNew(0); }, [page, pageSize, ns, selectedCluster?.id]);

  const handleDelete = useCallback(async (d) => {
    const key = `${d.namespace}/${d.name}`;
    if (!window.confirm(`Delete deployment "${d.name}" in namespace "${d.namespace}"? This cannot be undone.`)) return;
    setDeletingKey(key);
    try {
      await clustersAPI.deleteDeployment(selectedCluster.id, d.namespace, d.name);
      removeRow(key);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete deployment');
    } finally {
      setDeletingKey(null);
    }
  }, [selectedCluster?.id, removeRow]);

  const handleRestart = useCallback(async (d) => {
    const key = `${d.namespace}/${d.name}`;
    if (!window.confirm(`Restart deployment "${d.name}" in namespace "${d.namespace}"? This rolls every pod.`)) return;
    setRestartingKey(key);
    try {
      await clustersAPI.restartWorkload(selectedCluster.id, 'deployments', d.namespace, d.name);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to restart deployment');
    } finally {
      setRestartingKey(null);
    }
  }, [selectedCluster?.id]);

  if (!selectedCluster) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Deployments</h1></div>
        <div className="no-cluster-msg">
          No cluster selected. <span className="link" onClick={() => navigate('/clusters')}>Add or select a cluster</span> to view deployments.
        </div>
      </div>
    );
  }

  if (!selectedCluster.is_imported) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Deployments — {selectedCluster.name}</h1></div>
        <div className="no-cluster-msg">
          Live resource viewing is available for imported clusters only.
        </div>
      </div>
    );
  }

  // Guard every field: agent-cluster / malformed items can arrive without name or
  // namespace, and `d.name.toLowerCase()` on undefined crashed the whole page.
  // Namespace scoping is now server-side (only this ns is fetched); only name-filter here.
  const filtered = deployments.filter(d =>
    (d.name || '').toLowerCase().includes(nameFilter.toLowerCase())
  );

  return (
    <div className="resource-page">
      <div className="page-header">
        <div>
          <h1>Deployments</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" style={{ fontFamily: 'monospace' }}
            onClick={() => setShowTerminal(true)}>
            ⌨ Terminal
          </button>
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setShowDeploy(true)}>🚀 Deploy Image</button>
          )}
          <button className="btn btn-secondary" onClick={loadDeployments}>Refresh</button>
        </div>
      </div>

      {error && <div className="resource-error">{error}</div>}

      {loading ? (
        <div className="resource-loading">Loading deployments...</div>
      ) : (
        <>
          <div className="resource-filter">
            <NamespaceSelect namespaces={namespaces} ns={ns} onChange={setNs} />
            <input
              type="text"
              placeholder="Filter by name..."
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
            <span className="resource-count">{nameFilter ? `${filtered.length} of ${deployments.length} on page (${total} total)` : `${total} deployments`}</span>
            {paged && (
              <span title={liveConnected ? 'Live — updates pushed from the cluster' : 'Not live — showing a fetched snapshot'}
                    style={{ fontSize: 12, color: liveConnected ? 'var(--color-success-500)' : 'var(--muted)' }}>
                {liveConnected ? '● live' : '○ offline'}
              </span>
            )}
            {pendingNew > 0 && (
              <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }}
                onClick={() => { setPendingNew(0); loadDeployments(); }}>
                ↻ {pendingNew} new — refresh
              </button>
            )}
          </div>

          <div className="table-card">
            <table className="ds">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Namespace</th>
                  <th>Ready</th>
                  <th>Up-to-date</th>
                  <th>Available</th>
                  <th>Age</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No deployments found</td></tr>
                ) : filtered.map(d => (
                  <tr key={`${d.namespace}/${d.name}`}>
                    <td><code style={{ fontSize: 12 }}>{d.name}</code></td>
                    <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{d.namespace}</td>
                    <td>
                      <span style={{ color: d.ready_replicas === d.replicas ? 'var(--color-success-500)' : 'var(--color-danger-500)', fontWeight: 600 }}>
                        {d.ready_replicas}/{d.replicas}
                      </span>
                    </td>
                    <td>{d.updated_replicas}</td>
                    <td>{d.available_replicas}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      {(canEdit || canDelete) && (
                        <ActionsMenu>
                          {canEdit && (
                            <button style={MENU_ITEM} onClick={() => setDetail(d)}>
                              ✏️ Edit
                            </button>
                          )}
                          {canEdit && (
                            <button
                              style={MENU_ITEM}
                              disabled={restartingKey === `${d.namespace}/${d.name}`}
                              onClick={() => handleRestart(d)}
                              title="Rolling-restart every pod (kubectl rollout restart)"
                            >
                              {restartingKey === `${d.namespace}/${d.name}` ? 'Restarting…' : '↻ Restart'}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}
                              disabled={deletingKey === `${d.namespace}/${d.name}`}
                              onClick={() => handleDelete(d)}
                            >
                              {deletingKey === `${d.namespace}/${d.name}` ? 'Deleting…' : '🗑️ Delete'}
                            </button>
                          )}
                        </ActionsMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {paged && (
            <Pager
              page={page}
              pageSize={pageSize}
              total={total}
              loading={loading}
              onPage={setPage}
              onPageSize={setPageSize}
            />
          )}
        </>
      )}

      {showDeploy && (
        <DeployImageModal
          clusterId={selectedCluster.id}
          onClose={() => setShowDeploy(false)}
          onDeployed={() => { setShowDeploy(false); loadDeployments(); }}
        />
      )}

      {detail && selectedCluster && (
        <WorkloadDetailDrawer
          clusterId={selectedCluster.id}
          kind="Deployment"
          namespace={detail.namespace}
          name={detail.name}
          initialTab="yaml"
          autoEdit
          onClose={() => { setDetail(null); loadDeployments(); }}
        />
      )}

      {showTerminal && (
        <WebTerminal
          clusterId={selectedCluster.id}
          onClose={() => setShowTerminal(false)}
        />
      )}
    </div>
  );
}

export default Deployments;
